import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSocket } from './SocketContext';
import { useNotification } from './NotificationContext';
import moment from 'moment';

const WhatsAppContext = createContext(null);

export const useWhatsApp = () => {
  return useContext(WhatsAppContext);
};

export const WhatsAppProvider = ({ children }) => {
  // Obtener socket del contexto principal
  const socket = useSocket();
  const { showNotification } = useNotification();
  
  // Estados de WhatsApp
  const [connected, setConnected] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Referencias para evitar recreaciones
  const selectedChatRef = useRef(selectedChat);
  const messagesRef = useRef(messages);
  
  // Actualizar referencias
  selectedChatRef.current = selectedChat;
  messagesRef.current = messages;

  // Implementar emitWithAck en el socket existente
  useEffect(() => {
    if (socket && !socket.emitWithAck) {
      socket.emitWithAck = function (event, data) {
        return new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error(`Timeout esperando respuesta para evento: ${event}`));
          }, 15000);

          this.emit(event, data, (response) => {
            clearTimeout(timeout);
            if (response && response.error) {
              reject(new Error(response.message || response.error));
            } else {
              resolve(response);
            }
          });
        });
      };
    }
  }, [socket]);

  // Monitorear conexión del socket
  useEffect(() => {
    if (socket) {
      setConnected(socket.connected);
      setError(null);
      
      // Verificar autenticación de WhatsApp si el socket está conectado
      if (socket.connected && localStorage.getItem("whatsapp_authenticated") === "true") {
        socket.emit("checkAuthentication");
      }
    } else {
      setConnected(false);
    }
  }, [socket]);

  // Cargar estado autenticado desde localStorage
  useEffect(() => {
    const savedAuth = localStorage.getItem("whatsapp_authenticated");
    if (savedAuth === "true") {
      setAuthenticated(true);
      setLoading(false);
    }

    // Cargar chat seleccionado si existe
    const savedChatId = localStorage.getItem("whatsapp_selected_chat");
    if (savedChatId && authenticated && socket) {
      socket.emit("getChats");
    }
  }, [socket, authenticated]);

  // Función para determinar tipo de media desde mimetype
  const getMediaTypeFromMimetype = useCallback((mimetype) => {
    if (!mimetype) return "unknown";

    if (mimetype.startsWith("image/")) {
      if (mimetype === "image/webp") return "sticker";
      return "image";
    }

    if (mimetype.startsWith("video/")) return "video";
    if (mimetype.startsWith("audio/")) return "audio";

    const documentTypes = {
      "application/pdf": "pdf",
      "application/msword": "doc",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
      "application/vnd.ms-excel": "xls",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
      "application/vnd.ms-powerpoint": "ppt",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation": "pptx",
      "text/plain": "txt",
      "application/json": "json",
      "application/zip": "zip",
      "application/x-rar-compressed": "rar",
    };

    return documentTypes[mimetype] || "document";
  }, []);

  // Configurar eventos de WhatsApp
  useEffect(() => {
    if (!socket) return;

    // Evento: recibir código QR
    const handleQR = ({ qrImage }) => {
      setQrCode(qrImage);
      setLoading(false);
    };

    // Evento: autenticación exitosa
    const handleAuthenticated = () => {
      setAuthenticated(true);
      setQrCode(null);
      localStorage.setItem("whatsapp_authenticated", "true");
    };

    // Evento: cliente listo
    const handleReady = () => {
      setAuthenticated(true);
      setLoading(false);
      localStorage.setItem("whatsapp_authenticated", "true");
      socket.emit("getChats");
    };

    // Evento: recibir lista de chats
    const handleChats = (chatList) => {
      setChats(chatList);

      const savedChatId = localStorage.getItem("whatsapp_selected_chat");
      if (savedChatId && chatList.length > 0) {
        const chatToSelect = chatList.find((c) => c.id === savedChatId);
        if (chatToSelect) {
          setSelectedChat(chatToSelect);
          socket.emit("getChatMessages", { chatId: chatToSelect.id });
        }
      }
    };

    // Evento: recibir mensajes de un chat
    const handleChatMessages = ({ chatId, messages: chatMessages }) => {
      
      setMessages((prev) => ({
        ...prev,
        [chatId]: chatMessages,
      }));
      
      if (socket) {
        const messagesWithMedia = chatMessages.filter(
          msg => msg.hasMedia && msg.mediaStatus === "needsLoading"
        );
        
        if (messagesWithMedia.length > 0) {
          
          const priorityMessages = messagesWithMedia.slice(0, 5);
          
          priorityMessages.forEach((message, index) => {
            setTimeout(() => {
              setMessages(prev => {
                const currentMessages = prev[chatId] || [];
                return {
                  ...prev,
                  [chatId]: currentMessages.map(msg => 
                    msg.id === message.id ? { ...msg, mediaStatus: "loading" } : msg
                  )
                };
              });
              
              socket.emit("getMessageMedia", { 
                messageId: message.id,
                mediaMessageRef: message.mediaMessageRef
              }, (response) => {
                if (response && !response.error) {
                  setMessages(prev => {
                    const currentMessages = prev[chatId] || [];
                    return {
                      ...prev,
                      [chatId]: currentMessages.map(msg => 
                        msg.id === message.id ? {
                          ...msg,
                          mediaData: response,
                          mediaStatus: "loaded",
                          mediaType: response.type || getMediaTypeFromMimetype(response.mimetype)
                        } : msg
                      )
                    };
                  });
                } else {
                  setMessages(prev => {
                    const currentMessages = prev[chatId] || [];
                    return {
                      ...prev,
                      [chatId]: currentMessages.map(msg => 
                        msg.id === message.id ? {
                          ...msg,
                          mediaStatus: "error",
                          mediaError: response?.message || "Error al cargar multimedia"
                        } : msg
                      )
                    };
                  });
                }
              });
            }, index * 500);
          });
          
          if (messagesWithMedia.length > 5) {
            setTimeout(() => {
              messagesWithMedia.slice(5).forEach((message, index) => {
                setTimeout(() => {
                  setMessages(prev => {
                    const currentMsg = prev[chatId]?.find(m => m.id === message.id);
                    if (!currentMsg || currentMsg.mediaStatus !== "needsLoading") {
                      return prev;
                    }
                    
                    const currentMessages = prev[chatId] || [];
                    return {
                      ...prev,
                      [chatId]: currentMessages.map(msg => 
                        msg.id === message.id ? { ...msg, mediaStatus: "loading" } : msg
                      )
                    };
                  });
                  
                  socket.emit("getMessageMedia", { 
                    messageId: message.id,
                    mediaMessageRef: message.mediaMessageRef
                  }, (response) => {
                    if (response && !response.error) {
                      setMessages(prev => {
                        const currentMessages = prev[chatId] || [];
                        return {
                          ...prev,
                          [chatId]: currentMessages.map(msg => 
                            msg.id === message.id ? {
                              ...msg,
                              mediaData: response,
                              mediaStatus: "loaded",
                              mediaType: response.type || getMediaTypeFromMimetype(response.mimetype)
                            } : msg
                          )
                        };
                      });
                    } else {
                      setMessages(prev => {
                        const currentMessages = prev[chatId] || [];
                        return {
                          ...prev,
                          [chatId]: currentMessages.map(msg => 
                            msg.id === message.id ? {
                              ...msg,
                              mediaStatus: "error",
                              mediaError: response?.message || "Error al cargar multimedia"
                            } : msg
                          )
                        };
                      });
                    }
                  });
                }, index * 1000);
              });
            }, 5000);
          }
        }
      }
    };

    // Evento: recibir nuevo mensaje
    const handleMessage = async (message) => {
      console.log("Mensaje recibido:", message.id, message.hasMedia ? "[con multimedia]" : message.body);

      if (message.hasMedia) {
        try {
          message.mediaStatus = "loading";

          if (selectedChatRef.current && message.chatId === selectedChatRef.current.id) {
            setMessages((prev) => {
              const currentMessages = prev[selectedChatRef.current.id] || [];
              const messageExists = currentMessages.some((m) => m.id === message.id);

              if (messageExists) {
                return {
                  ...prev,
                  [selectedChatRef.current.id]: currentMessages.map((m) =>
                    m.id === message.id ? { ...m, mediaStatus: "loading" } : m
                  ),
                };
              } else {
                return {
                  ...prev,
                  [selectedChatRef.current.id]: [...currentMessages, message],
                };
              }
            });
          }

          console.log("Solicitando multimedia para mensaje:", message.id);

          const getMediaWithRetry = async (retries = 3) => {
            try {
              return new Promise((resolve, reject) => {
                socket.emit("getMessageMedia", { messageId: message.id }, (response) => {
                  if (response && response.error) {
                    reject(new Error(response.message || response.error));
                  } else {
                    resolve(response);
                  }
                });

                setTimeout(() => reject(new Error("Timeout getting media")), 15000);
              });
            } catch (error) {
              if (retries > 0) {
                console.log(`Reintentando obtener multimedia (${retries} intentos restantes)...`);
                await new Promise((resolve) => setTimeout(resolve, 2000));
                return getMediaWithRetry(retries - 1);
              }
              throw error;
            }
          };

          try {
            const mediaData = await getMediaWithRetry();

            message.mediaData = mediaData;
            message.mediaStatus = "loaded";
            message.mediaType = mediaData.type || getMediaTypeFromMimetype(mediaData.mimetype);

            console.log("Multimedia cargada correctamente:", message.id, mediaData.mimetype);

            if (selectedChatRef.current && message.chatId === selectedChatRef.current.id) {
              setMessages((prev) => {
                const currentMessages = prev[selectedChatRef.current.id] || [];
                return {
                  ...prev,
                  [selectedChatRef.current.id]: currentMessages.map((m) =>
                    m.id === message.id
                      ? {
                          ...m,
                          mediaData,
                          mediaStatus: "loaded",
                          mediaType: mediaData.type || getMediaTypeFromMimetype(mediaData.mimetype),
                        }
                      : m
                  ),
                };
              });
            }
          } catch (mediaError) {
            console.error("Error cargando multimedia:", mediaError);
            message.mediaStatus = "error";
            message.mediaError = mediaError.message;

            if (selectedChatRef.current && message.chatId === selectedChatRef.current.id) {
              setMessages((prev) => {
                const currentMessages = prev[selectedChatRef.current.id] || [];
                return {
                  ...prev,
                  [selectedChatRef.current.id]: currentMessages.map((m) =>
                    m.id === message.id
                      ? {
                          ...m,
                          mediaStatus: "error",
                          mediaError: mediaError.message,
                        }
                      : m
                  ),
                };
              });
            }
          }
        } catch (error) {
          console.error("Error general procesando multimedia:", error);
          message.mediaStatus = "error";
          message.mediaError = error.message;
        }
      }

      if (selectedChatRef.current && message.chatId === selectedChatRef.current.id) {
        setMessages((prev) => {
          const currentMessages = prev[selectedChatRef.current.id] || [];
          const tempIndex = currentMessages.findIndex(
            (m) =>
              m.body === message.body &&
              m.id.startsWith("temp_") &&
              m.fromMe === true
          );

          if (tempIndex >= 0) {
            const updatedMessages = [...currentMessages];
            updatedMessages[tempIndex] = {
              ...message,
              mediaData: message.mediaData || updatedMessages[tempIndex].mediaData,
              mediaStatus: message.mediaStatus || updatedMessages[tempIndex].mediaStatus,
            };
            return {
              ...prev,
              [selectedChatRef.current.id]: updatedMessages,
            };
          } else if (!currentMessages.some((m) => m.id === message.id)) {
            return {
              ...prev,
              [selectedChatRef.current.id]: [...currentMessages, message],
            };
          }

          return prev;
        });
      }

      setChats((prev) => {
        const updatedChats = prev.map((chat) => {
          if (chat.id === message.chatId) {
            return {
              ...chat,
              lastMessage: {
                body: message.hasMedia
                  ? message.mediaType
                    ? `[${message.mediaType}]`
                    : "[Media]"
                  : message.body,
                fromMe: message.fromMe,
              },
              timestamp: message.timestamp,
              unreadCount:
                selectedChatRef.current && selectedChatRef.current.id === chat.id
                  ? 0
                  : (chat.unreadCount || 0) + 1,
            };
          }
          return chat;
        });

        return updatedChats.sort((a, b) => {
          if (!a.timestamp) return 1;
          if (!b.timestamp) return -1;
          return moment(b.timestamp).diff(moment(a.timestamp));
        });
      });
    };

    // Evento: cambio de estado de mensaje
    const handleMessageAck = ({ id, status, ack }) => {
      console.log(`Actualización de estado de mensaje - ID: ${id}, Estado: ${status}, ACK: ${ack}`);

      setMessages((prevMessages) => {
        const updatedMessages = { ...prevMessages };

        Object.keys(updatedMessages).forEach((chatId) => {
          const chatMessages = updatedMessages[chatId] || [];

          if (chatMessages.length === 0) return;

          updatedMessages[chatId] = chatMessages.map((msg) => {
            if (msg.id === id) {
              return {
                ...msg,
                status,
                ack,
                isTemp: false,
              };
            }

            if (msg.id.startsWith("temp_") && msg.fromMe) {
              if (msg.tempId === id) {
                return {
                  ...msg,
                  status,
                  ack,
                  confirmedId: id,
                };
              }

              const msgTime = moment(msg.timestamp);
              const now = moment();
              const isRecent = now.diff(msgTime, "seconds") < 60;

              const pendingCount = chatMessages.filter(
                (m) => m.id.startsWith("temp_") && m.fromMe && m !== msg
              ).length;

              if (isRecent && pendingCount === 0) {
                return {
                  ...msg,
                  status,
                  ack,
                  confirmedId: id,
                };
              }
            }

            return msg;
          });
        });

        return updatedMessages;
      });

      setChats((prevChats) => {
        return prevChats.map((chat) => {
          if (chat.lastMessage && chat.lastMessage.fromMe) {
            return {
              ...chat,
              lastMessage: {
                ...chat.lastMessage,
                status,
              },
            };
          }
          return chat;
        });
      });
    };

    // Evento: resultados de búsqueda
    const handleSearchResults = (results) => {
      setSearchResults(results);
    };

    // Evento de error general
    const handleError = ({ message }) => {
      setError(message);
      showNotification({
        message: `Error de WhatsApp: ${message}`,
        severity: 'error',
        autoHideDuration: 6000,
      });
    };

    // Registrar eventos
    socket.on("whatsapp:qr", handleQR);
    socket.on("whatsapp:authenticated", handleAuthenticated);
    socket.on("whatsapp:ready", handleReady);
    socket.on("chats", handleChats);
    socket.on("chatMessages", handleChatMessages);
    socket.on("whatsapp:message", handleMessage);
    socket.on("whatsapp:message_ack", handleMessageAck);
    socket.on("searchResults", handleSearchResults);
    socket.on("error", handleError);

    return () => {
      socket.off("whatsapp:qr", handleQR);
      socket.off("whatsapp:authenticated", handleAuthenticated);
      socket.off("whatsapp:ready", handleReady);
      socket.off("chats", handleChats);
      socket.off("chatMessages", handleChatMessages);
      socket.off("whatsapp:message", handleMessage);
      socket.off("whatsapp:message_ack", handleMessageAck);
      socket.off("searchResults", handleSearchResults);
      socket.off("error", handleError);
    };
  }, [socket, getMediaTypeFromMimetype, showNotification]);

  // Seleccionar un chat
  const selectChat = useCallback((chat) => {
    setSelectedChat(chat);
    localStorage.setItem("whatsapp_selected_chat", chat.id);

    setChats((prev) =>
      prev.map((c) => (c.id === chat.id ? { ...c, unreadCount: 0 } : c))
    );

    if (socket) {
      socket.emit("getChatMessages", { chatId: chat.id });
    }
  }, [socket]);

  // Enviar mensaje con actualización local inmediata
  const sendMessage = useCallback((text) => {
    if (socket && selectedChat) {
      const tempId = `temp_${Date.now()}`;

      const tempMessage = {
        id: tempId,
        uuid: tempId,
        body: text,
        timestamp: moment().format("YYYY-MM-DD HH:mm:ss"),
        fromMe: true,
        status: "PENDING",
        authorName: "Tú",
        isTemp: true,
        sentTime: Date.now(),
      };

      console.log("Enviando mensaje temporal:", tempMessage.id);

      setMessages((prev) => {
        const currentMessages = prev[selectedChat.id] || [];
        return {
          ...prev,
          [selectedChat.id]: [...currentMessages, tempMessage],
        };
      });

      setChats((prev) => {
        const updatedChats = prev.map((chat) => {
          if (chat.id === selectedChat.id) {
            return {
              ...chat,
              lastMessage: {
                body: text,
                fromMe: true,
              },
              timestamp: tempMessage.timestamp,
            };
          }
          return chat;
        });

        return updatedChats.sort((a, b) => {
          if (!a.timestamp) return 1;
          if (!b.timestamp) return -1;
          return moment(b.timestamp).diff(moment(a.timestamp));
        });
      });

      socket.emit("sendMessage", {
        chatId: selectedChat.id,
        message: text,
        tempId,
      });
    }
  }, [socket, selectedChat]);

  // Buscar mensajes
  const searchMessages = useCallback((query) => {
    setSearchQuery(query);

    if (socket && query.trim()) {
      socket.emit("searchMessages", { query });
    } else {
      setSearchResults([]);
    }
  }, [socket]);

  // Función para reintentar carga de multimedia
  const handleRetryMedia = useCallback((messageId) => {
    const id = typeof messageId === 'object' ? messageId.id : messageId;
    
    if (!socket || !selectedChat) {
      console.error("Socket no disponible o chat no seleccionado");
      return;
    }
    
    const targetMessage = selectedChat && messages[selectedChat.id]?.find(m => m.id === id);
    
    if (!targetMessage) {
      console.error("No se encontró el mensaje para recargar");
      return;
    }
    
    setMessages(prev => {
      const chatMessages = prev[selectedChat.id] || [];
      return {
        ...prev,
        [selectedChat.id]: chatMessages.map(msg => 
          msg.id === id ? { ...msg, mediaStatus: 'loading' } : msg
        )
      };
    });
    
    socket.emit('getMessageMedia', { 
      messageId: id,
      mediaMessageRef: targetMessage.mediaMessageRef
    }, (response) => {
      if (!response || response.error) {
        setMessages(prev => {
          const chatMessages = prev[selectedChat.id] || [];
          return {
            ...prev,
            [selectedChat.id]: chatMessages.map(msg => 
              msg.id === id ? { 
                ...msg, 
                mediaStatus: 'error', 
                mediaError: response?.message || response?.error || "Error desconocido" 
              } : msg
            )
          };
        });
        return;
      }
      
      setMessages(prev => {
        const chatMessages = prev[selectedChat.id] || [];
        return {
          ...prev,
          [selectedChat.id]: chatMessages.map(msg => 
            msg.id === id ? { 
              ...msg, 
              mediaData: response,
              mediaStatus: 'loaded',
              mediaType: response.type || getMediaTypeFromMimetype(response.mimetype)
            } : msg
          )
        };
      });
    });
  }, [socket, selectedChat, messages, getMediaTypeFromMimetype]);

  // Valor del contexto memoizado para evitar re-renders
  const contextValue = useMemo(() => ({
    connected,
    authenticated,
    qrCode,
    chats,
    selectedChat,
    messages: selectedChat ? messages[selectedChat.id] || [] : [],
    searchQuery,
    searchResults,
    loading,
    error,
    selectChat,
    sendMessage,
    searchMessages,
    handleRetryMedia,
    getMediaTypeFromMimetype,
    socket,
  }), [
    connected,
    authenticated,
    qrCode,
    chats,
    selectedChat,
    messages,
    searchQuery,
    searchResults,
    loading,
    error,
    selectChat,
    sendMessage,
    searchMessages,
    handleRetryMedia,
    getMediaTypeFromMimetype,
    socket
  ]);

  return (
    <WhatsAppContext.Provider value={contextValue}>
      {children}
    </WhatsAppContext.Provider>
  );
};

export default WhatsAppProvider;